import type { TenantType } from "@closer/shared";

/**
 * Sidebar gezinme KAYDI (CLOS-96 `[057]`).
 *
 * ### Neden `apps/web`'te, `packages/ui`'de değil
 *
 * Kayıt domain biliyor (`contract`, `organization`); `packages/ui/CLAUDE.md` bunu yasaklıyor
 * — *"bileşen domain bilmez"*. UI paketi yalnız sunumu alıyor, hangi öğelerin var olduğunu
 * bilmiyor.
 *
 * ### Kanonik sıra korunur, EKSİK ÖĞE ÇİZİLMEZ
 *
 * Kart iki şeyi birden söylüyor: *"Kanonik sıra korunur"* ve *"henüz kullanılamayan veya
 * yetkisiz bir domain görünür ve çalışır navigasyon olarak sunulmaz."* İkisi çelişmiyor:
 * sıra bu dizide sabit, ama `route` alanı `null` olan öğe hiç render edilmiyor.
 *
 * Sayfası olmayan öğeye yer tutucu koymak ADR-0014 ihlali olurdu (kartsız yüzey) ve
 * kullanıcıyı tıklayınca hiçbir şey olmayan bir bağlantıya götürürdü. Bugün yalnız Ana
 * Sayfa'nın rotası var; diğerleri kendi kartlarıyla `route` kazanacak.
 *
 * ### Bekleyen İşlemler BURADA DEĞİL
 *
 * CLOS-96 sidebar'ın kapsamını altı domainle sayıyor: Ana Sayfa, Sözleşmeler, Müvekkiller,
 * Müvekkil Alanı, Organizasyon Yönetimi, Abonelik ve Kullanım. Bekleyen İşlemler bunlardan
 * biri değil — CLOS-181'in kanonik referansı *"D01 Ana Sayfa; Üst Bar"*, yani liste Ana
 * Sayfa'nın bölümü ve üst bardaki rozetli panelden ulaşılan bir yüzey.
 *
 * ⚠ Bir süre bu kayıtta durdu. Sebep yanlış bir teşhisti: Ana Sayfa'daki "tam listeyi aç"
 * bağlantısı `/app` önekini kaybettiği için hiçbir yere gitmiyordu ve "tam listeye giriş
 * yok" sanıldı. Bağlantı onarıldı (CLOS-181), kayıt kaldırıldı — kartsız yüzey ADR-0014
 * ihlalidir ve gerçek kusurun üstünü örtüyordu.
 *
 * ### Görünürlük bir GÜVENLİK SINIRI DEĞİL
 *
 * Menüyü gizlemek bir kolaylıktır; gerçek kapı her uçtaki `requirePermission`. Kart da
 * bunu şart koşuyor: *"yalnız istemci tarafında menü gizlemeye dayanmaz."* Buradaki
 * hesap, kullanıcıyı 403 duvarına toslatmak yerine yapabileceği şeye götürüyor.
 */

export interface NavItemDef {
  key: string;
  /**
   * KANONİK DOMAIN — CLOS-96 `[057]`'nin saydığı altı başlıktan biri.
   *
   * ⚠ `key`'den AYRI ve sebebi ölçülmüş bir kusur (CLOS-842). Bir domainin BİRDEN ÇOK
   * ekranı olabiliyor: Müvekkil Alanı'nın büro tarafı (`client-portal`) ve müvekkil
   * tarafı (`client-workspace`) ayrı kayıtlar ama aynı domain. `key` üzerinden ölçen bir
   * bekçi kanonik listeyi yedi satır sanıyordu; kart altı domain sayıyor.
   *
   * Bu alan sayesinde bekçi kaydın YAPISINI değil, kartın saydığı DOMAİNLERİ ölçüyor.
   */
  domain: string;
  label: string;
  /**
   * Rota — `null` ise sayfa HENÜZ YOK ve öğe çizilmiyor. Kanonik sıradaki yerini
   * koruyor ki ilgili kart geldiğinde tek satır değişsin.
   */
  route: string | null;
  /** Gerektiren yetki; `null` ise herkese açık (Ana Sayfa). */
  permission: string | null;
  /**
   * Hangi çalışma alanı türlerinde görünür. Kişisel alanda organizasyon işlemleri yok;
   * müvekkil domaini yalnız hukuk bürosunda (`permissionsForTenantType` ile aynı kural).
   */
  tenantTypes: readonly TenantType[];
}

const ENTERPRISE: readonly TenantType[] = ["enterprise_company", "enterprise_law_firm"];
/**
 * Sözleşme yüzeyi kişisel alanda da açık (CLOS-636).
 *
 * ⚠ Kişisel alan bu listeden dışarıda kaldığında kullanıcı yalnız "Ana Sayfa" görüyordu:
 * `individual_free` paketi 5 sözleşme hakkı satarken kullanılacak ekran yoktu. Menü bir
 * güvenlik sınırı değil; gerçek kapı `requirePermission` ve kişisel alanda o kapı artık
 * sahiplik üzerinden açılıyor.
 */
const SOZLESME_ALANLARI: readonly TenantType[] = ["individual", ...ENTERPRISE];
/**
 * ⚠ `client_workspace` de DAHİL (CLOS-629). Dışarıda kaldığında müvekkilin alanında Ana
 * Sayfa bile çizilmiyordu: kullanıcı boş bir sidebar görür ve uygulamada gidecek yeri
 * kalmazdı — testin yetki filtresi için savunduğu kuralın aynısı.
 */
const HEPSI: readonly TenantType[] = ["individual", ...ENTERPRISE, "client_workspace"];

/** ⚠ SIRA KANONİKTİR (kart kapsamı). Yeni öğe araya değil, kartının söylediği yere girer. */
export const NAV_ITEMS: readonly NavItemDef[] = [
  {
    key: "home",
    domain: "home",
    label: "Ana Sayfa",
    route: "/app",
    permission: null,
    tenantTypes: HEPSI,
  },
  {
    key: "contracts",
    domain: "contracts",
    label: "Sözleşmeler",
    /**
     * CLOS-624 ile açıldı. Liste sayfası `[083]` ile teslim edilmişti ama menü kaydı
     * güncellenmediği için ekranlara yalnız üst bardaki "Yeni Sözleşme" düğmesinden veya
     * adres yazarak ulaşılabiliyordu.
     */
    route: "/app/sozlesmeler",
    permission: "contract.view",
    tenantTypes: SOZLESME_ALANLARI,
  },
  {
    key: "clients",
    domain: "clients",
    label: "Müvekkiller",
    route: "/app/muvekkiller", // CLOS-169 `[116]` ile açıldı
    permission: "client.view",
    /**
     * ⚠ KİŞİSEL ALAN DA LİSTEDE (CLOS-813) — ama bu, orada herkese açık demek DEĞİL.
     *
     * Görünürlük iki koşulun kesişimi: tür listede olacak VE sunucunun verdiği yetki
     * kümesinde `client.view` bulunacak. Kişisel alanda o anahtarı yalnız `independent_lawyer`
     * arketipi alıyor (`permissionsForTenantType`), yani `self` kullanıcı türü listede
     * olmasına rağmen öğeyi GÖRMÜYOR.
     *
     * Karar neden burada değil sunucuda: menü arketipi okuyup kendi kararını üretseydi kural
     * iki yerde yazılmış olurdu ve doğrudan adresle girişte kapı ile menü ayrışabilirdi.
     * Menü yalnız sunucunun cevabını yansıtıyor.
     */
    tenantTypes: ["individual", "enterprise_law_firm"],
  },
  {
    key: "client-portal",
    domain: "client-workspace",
    label: "Müvekkil Alanı",
    route: null, // EP12 / CLOS-175 — SPONSOR tarafı: büronun sponsorlu alanları yönetmesi
    permission: "client.view",
    tenantTypes: ["enterprise_law_firm"],
  },
  /**
   * MÜVEKKİLİN KENDİ tarafı (CLOS-629 / `[123]`).
   *
   * ⚠ Üstteki `client-portal` ile aynı etiketi taşıyor ama aynı ekran DEĞİL: o, büronun
   * sponsorlu alanları yönettiği yüzey (`client.view`, hukuk bürosu); bu, müvekkilin kendi
   * alanı (`organization.view`, `client_workspace`). İkisi hiçbir zaman aynı anda
   * çizilmiyor çünkü tür kapsamları ayrık — kullanıcı iki "Müvekkil Alanı" görmez.
   */
  {
    key: "client-workspace",
    domain: "client-workspace",
    label: "Müvekkil Alanı",
    route: "/app/muvekkil-alani",
    permission: "organization.view",
    tenantTypes: ["client_workspace"],
  },
  {
    key: "organization",
    domain: "organization",
    label: "Organizasyon Yönetimi",
    route: "/app/organizasyon", // `[050]` ile açıldı
    permission: "organization.view",
    tenantTypes: ENTERPRISE,
  },
  /**
   * ABONELİK VE KULLANIM (D06 — CLOS-197 `[173]`).
   *
   * ⚠ `tenantTypes` KİŞİSEL ALANI DA KAPSIYOR ve bu kartın açık kriteri: *"Individual
   * kullanıcı yalnız kişisel çalışma alanına ait ticari ve kullanım bilgisini görür."*
   * Kişisel alanın `individual_free` paketi ve kapasite sayaçları var; Enterprise'a
   * kısıtlasaydık kullanıcı kendi planını hiçbir yerden göremezdi.
   *
   * ⚠ Sponsorlu müvekkil alanı DIŞARIDA: o alanın kendi paketi yok, lisansı sponsorun
   * hakkından geliyor — gösterilecek abonelik de yok.
   *
   * Görünürlüğün ikinci kapısı `subscription.view`; kişisel alanda o yetki SAHİPLİKTEN
   * geliyor (rol yok), kurumsal alanda Organizasyon Yöneticisi rolünden.
   */
  {
    key: "subscription",
    domain: "subscription",
    label: "Abonelik ve Kullanım",
    route: "/app/abonelik",
    permission: "subscription.view",
    tenantTypes: [...ENTERPRISE, "individual"],
  },
];

/**
 * Çizilecek öğeleri kanonik sırayla döndürür.
 *
 * Üç filtre birlikte: rotası olmayan (sayfa yok), tür kapsamı dışında olan ve yetkisi
 * olmayan öğe çıkarılır. `can` sunucudan gelen etkin yetki kümesini soruyor (`[043]`).
 */
export function visibleNavItems(
  tenantType: TenantType | undefined,
  can: (permission: string) => boolean,
  /**
   * Kayıt PARAMETRE: bugün rotası olan tek öğe yetki istemiyor, yani gerçek kayıtla
   * yetki filtresi hiç sınanamaz ve sessizce ölü kalabilirdi. Test kendi kaydını
   * verebiliyor; üretimde varsayılan kullanılıyor.
   */
  items: readonly NavItemDef[] = NAV_ITEMS,
): NavItemDef[] {
  if (!tenantType) return [];
  return items.filter(
    (item) =>
      item.route !== null &&
      item.tenantTypes.includes(tenantType) &&
      (item.permission === null || can(item.permission)),
  );
}
